import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import ScrollAnimation from "react-animate-on-scroll";
import DynamicBlogLoader from "../../utils/dynamicBlogLoader";
import ImageHelper from "../../utils/imageHelper";
import Pagetitle from "../elements/Pagetitle";


function BlogCarousel() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const allBlogs = await DynamicBlogLoader.loadAllBlogs();
      setBlogs(allBlogs.slice(0, 6)); // Show latest 6 blogs
      setError(null);
    } catch (err) {
      setError('Không thể tải bài viết');
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const formatDate = (dateString) => {
    return DynamicBlogLoader.formatDate(dateString);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technology': '#667eea',
      'Business': '#f093fb',
      'Crypto': '#4ecdc4',
      'Startup': '#ff6b6b',
      'Tutorial': '#45b7d1',
      'Reviews': '#96ceb4'
    };
    return colors[category] || '#6c757d';
  };

  if (loading) {
    return (
      <section id="blog-carousel" className="section">
        <div className="container">
          <Pagetitle title="Bài Viết Mới Nhất" />
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Đang tải...</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="blog-carousel" className="section">
        <div className="container">
          <Pagetitle title="Bài Viết Mới Nhất" />
          <div className="text-center">
            <div className="alert alert-warning">
              <p>{error}</p>
              <button onClick={loadBlogs} className="btn btn-primary btn-sm">
                Thử lại
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog-carousel" className="section">
      <div className="container">
        <ScrollAnimation
          animateIn="fadeInUp"
          animateOut="fadeInOut"
          animateOnce={true}
        >
          <Pagetitle title="Bài Viết Mới Nhất" />
        </ScrollAnimation>

        <ScrollAnimation
          animateIn="fadeInUp"
          animateOut="fadeInOut"
          animateOnce={true}
          delay={200}
        >
          <div className="blog-carousel-wrapper">
            {blogs.length > 0 ? (
              <Slider {...sliderSettings}>
                {blogs.map((blog) => (
                  <div key={blog.id} className="blog-slide">
                    <div className="blog-card">
                      <div className="blog-image-wrapper">
                        <Link to={`/bai-viet/${blog.slug}`}>
                          <img 
                            src={blog.metadata.image} 
                            alt={blog.metadata.title}
                            className="blog-image"
                            onError={ImageHelper.createImageErrorHandler({
                              width: 400,
                              height: 200,
                              text: blog.metadata.category,
                              color: getCategoryColor(blog.metadata.category)
                            })}
                          />
                        </Link>
                        <div className="blog-overlay">
                          <div className="blog-category">
                            <span 
                              className="category-badge"
                              style={{ backgroundColor: getCategoryColor(blog.metadata.category) }}
                            >
                              {blog.metadata.category}
                            </span>
                          </div>
                          {blog.metadata.featured && (
                            <div className="featured-badge">
                              <i className="fas fa-star"></i>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="blog-content">
                        <div className="blog-meta">
                          <span className="blog-date">
                            <i className="far fa-calendar-alt"></i>
                            {formatDate(blog.metadata.date)}
                          </span>
                          <span className="reading-time">
                            <i className="far fa-clock"></i>
                            {blog.readingTime} phút đọc
                          </span>
                        </div>
                        
                        <h3 className="blog-title">
                          <Link to={`/bai-viet/${blog.slug}`}>
                            {blog.metadata.title}
                          </Link>
                        </h3>
                        
                        <p className="blog-excerpt">
                          {blog.metadata.description || blog.excerpt}
                        </p>
                        
                        <div className="blog-tags">
                          {blog.metadata.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="blog-tag">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        
                        <div className="blog-footer">
                          <div className="blog-author">
                            <span>
                              <i className="far fa-user"></i>
                              {blog.metadata.author}
                            </span>
                          </div>
                          <Link 
                            to={`/bai-viet/${blog.slug}`}
                            className="read-more-btn"
                          >
                            Đọc tiếp
                            <i className="fas fa-arrow-right"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <div className="text-center">
                <p>Chưa có bài viết nào.</p>
              </div>
            )}
          </div>
        </ScrollAnimation>

        <ScrollAnimation
          animateIn="fadeInUp"
          animateOut="fadeInOut"
          animateOnce={true}
          delay={400}
        >
          <div className="text-center blog-actions">
            <div className="spacer" data-height="30"></div>
            <Link to="/bai-viet" className="btn btn-default btn-lg">
              <i className="fas fa-book-open"></i>
              Xem tất cả bài viết
            </Link>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
}

export default BlogCarousel;